import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API, formatApiError } from "@/lib/api";
import { useRoom } from "@/hooks/useRoom";
import { useT } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

import { Switch } from "@/components/ui/switch";
import { CheckCircle, WifiHigh, WifiSlash } from "@phosphor-icons/react";
import { Logo } from "@/components/Logo";

export default function Judge() {
  const { eventId, ringId, pwd } = useParams();
  const t = useT();
  const [session, setSession] = useState(() => JSON.parse(localStorage.getItem("judge_session") || "null"));
  const [manual, setManual] = useState({ ring_password: pwd || "", name: "" });
  const [error, setError] = useState("");

  const validSession = session && session.ring?.id === ringId;
  const token = validSession ? session.token : null;
  const { snapshot, connected, send } = useRoom(validSession ? ringId : null, token);

  const doLogin = useCallback(async (password, name) => {
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/judge-login`, { event_id: eventId, ring_id: ringId, ring_password: password, name: (name || "").trim() || "Giudice" });
      localStorage.setItem("judge_session", JSON.stringify(data));
      setSession(data);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    }
  }, [eventId, ringId]);

  useEffect(() => { if (pwd) setManual((m) => ({ ...m, ring_password: pwd })); }, [pwd]);

  const judgeAction = async (obj, restPath, restBody) => {
    if (send(obj)) return;
    try { await axios.post(`${API}${restPath}`, restBody, { headers: { Authorization: `Bearer ${token}` } }); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!validSession) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <form onSubmit={(e) => { e.preventDefault(); doLogin(manual.ring_password, manual.name); }} className="w-full max-w-xs space-y-4 bg-card border border-border rounded-lg p-6" data-testid="judge-login-form">
          <h1 className="flex items-center gap-2 text-xl font-heading font-extrabold uppercase text-brand-yellow"><Logo size={24} className="text-brand-yellow" /> {t("j.access")}</h1>
          <p className="text-xs text-muted-foreground">Ring: {ringId?.slice(0, 8)}…</p>
          <Input placeholder={t("j.yourName")} value={manual.name} onChange={(e) => setManual({ ...manual, name: e.target.value })} data-testid="judge-name" />
          <Input placeholder={t("ring.password")} value={manual.ring_password} onChange={(e) => setManual({ ...manual, ring_password: e.target.value })} data-testid="judge-password" />
          {error && <p className="text-sm text-destructive" data-testid="judge-error">{error}</p>}
          <Button type="submit" className="w-full" disabled={!manual.name.trim() || !manual.ring_password} data-testid="judge-login-submit">{t("login.enter")}</Button>
        </form>
      </div>
    );
  }

  const match = snapshot?.match;
  const ready = snapshot?.judges?.find((jj) => jj.judge_id === session.judge_id)?.ready;

  const setReady = (v) => judgeAction({ type: "ready", ready: v }, "/judge/ready", { ready: v });

  const leave = async () => {
    try { await axios.post(`${API}/judge/logout`, {}, { headers: { Authorization: `Bearer ${token}` } }); } catch (e) { console.error("judge logout failed:", e); }
    localStorage.removeItem("judge_session");
    setSession(null);
  };

  return (
    <div className="min-h-screen bg-black text-white flex flex-col">
      <header className="h-12 flex items-center justify-between px-4 border-b border-neutral-800 shrink-0">
        <span className="font-bold text-sm">{session.ring.name} · {t("rc.judges")}</span>
        <div className="flex items-center gap-3">
          {connected ? <WifiHigh className="text-medical" size={18} data-testid="judge-online" /> : <WifiSlash className="text-hong" size={18} data-testid="judge-offline" />}
          <div className="flex items-center gap-2">
            <span className="text-xs text-neutral-400">{t("j.ready")}</span>
            <Switch checked={!!ready} onCheckedChange={setReady} data-testid="judge-ready-switch" />
          </div>
          <button onClick={leave} className="text-[11px] text-neutral-500 hover:text-white underline underline-offset-2" data-testid="judge-leave">{t("j.leave")}</button>
        </div>
      </header>

      {!match ? (
        <div className="flex-1 flex items-center justify-center text-neutral-500">{t("j.waitingMatch")}</div>
      ) : match.discipline === "combat" ? (
        <CombatJudge match={match} judgeAction={judgeAction} token={token} />
      ) : (
        <PoomsaeJudge match={match} judgeAction={judgeAction} resetTs={snapshot?.ring?.reset_ts} ready={!!ready} requireReady={match.state?.require_judges !== false} />
      )}
    </div>
  );
}

function VoteBtn({ color, points, onClick, testid }) {
  return (
    <Button onClick={onClick} data-testid={testid}
      className={`flex-1 h-full text-4xl font-score rounded-none ${color === "red" ? "bg-hong hover:bg-hong/90" : "bg-chung hover:bg-chung/90"} active:scale-95`}>
      +{points}
    </Button>
  );
}

function CombatJudge({ match, judgeAction }) {
  const t = useT();
  const vote = (side, points) => judgeAction({ type: "vote", side, points }, "/judge/vote", { side, points });
  return (
    <div className="flex-1 flex flex-col">
      <div className="text-center py-2 text-xs uppercase tracking-widest text-neutral-500">{t("j.assignPoints")}</div>
      <div className="flex-1 flex gap-1 pb-1">
        <div className="flex-1 flex flex-col gap-1">
          <div className="text-center py-1 bg-hong/20 text-hong font-name text-2xl uppercase truncate">{nameOf(match, match.red_athlete_id)}</div>
          <div className="flex-1 flex gap-1">
            <VoteBtn color="red" points={1} onClick={() => vote("red", 1)} testid="judge-red-1" />
            <VoteBtn color="red" points={2} onClick={() => vote("red", 2)} testid="judge-red-2" />
          </div>
        </div>
        <div className="flex-1 flex flex-col gap-1">
          <div className="text-center py-1 bg-chung/20 text-chung font-name text-2xl uppercase truncate">{nameOf(match, match.blue_athlete_id)}</div>
          <div className="flex-1 flex gap-1">
            <VoteBtn color="blue" points={1} onClick={() => vote("blue", 1)} testid="judge-blue-1" />
            <VoteBtn color="blue" points={2} onClick={() => vote("blue", 2)} testid="judge-blue-2" />
          </div>
        </div>
      </div>
    </div>
  );
}

function PoomsaeJudge({ match, judgeAction, resetTs, ready, requireReady }) {
  const t = useT();
  const [technical, setTechnical] = useState(4.0);
  const [energy, setEnergy] = useState(2.0);
  const [balance, setBalance] = useState(2.0);
  const [rhythm, setRhythm] = useState(2.0);
  const [sent, setSent] = useState(false);
  const form = match.state?.current_form || 1;
  const pst = match.state || {};
  const versus = pst.mode === "versus";
  const perfSide = pst.current_side || "red";
  const activeId = versus ? (perfSide === "red" ? match.red_athlete_id : match.blue_athlete_id) : (match.athlete_id || match.red_athlete_id);

  useEffect(() => { setTechnical(4.0); setEnergy(2.0); setBalance(2.0); setRhythm(2.0); setSent(false); }, [form, resetTs]);

  const clamp = (v) => Math.max(0, Math.min(4, Math.round(v * 10) / 10));
  const total = Math.round((technical + energy + balance + rhythm) * 100) / 100;

  const submit = () => {
    const payload = { technical, energy, balance, rhythm, total };
    judgeAction({ type: "poomsae_vote", ...payload }, "/judge/poomsae-vote", payload);
    setSent(true);
    toast.success(t("j.voteSent", { v: total.toFixed(2) }));
  };

  const clampBar = (v) => Math.max(0, Math.min(2, Math.round(v * 10) / 10));

  const Bar = ({ label, value, set, testid }) => (
    <div className="flex items-center gap-3 py-1">
      <span className="text-base flex-1 min-w-0 truncate">{label}</span>
      <button onClick={() => set((x) => clampBar(x - 0.1))} disabled={value <= 0}
        className="h-14 w-14 rounded-lg bg-warn text-black text-2xl font-bold flex items-center justify-center active:scale-95 transition-transform disabled:opacity-30"
        data-testid={`${testid}-minus`}>−</button>
      <span className="font-mono-t tabular text-white text-2xl w-12 text-center" data-testid={`${testid}-val`}>{value.toFixed(1)}</span>
      <button onClick={() => set((x) => clampBar(x + 0.1))} disabled={value >= 2}
        className="h-14 w-14 rounded-lg bg-medical text-black text-2xl font-bold flex items-center justify-center active:scale-95 transition-transform disabled:opacity-30"
        data-testid={`${testid}-plus`}>+</button>
    </div>
  );

  return (
    <div className="flex-1 overflow-y-auto p-5 max-w-md mx-auto w-full">
      <div className="text-center text-chung text-xs uppercase tracking-widest">{t("j.form")} {form} · {match.label}</div>
      <div className={`text-center font-name text-3xl uppercase mt-1 mb-5 ${versus ? (perfSide === "red" ? "text-hong" : "text-chung") : ""}`}>{nameOf(match, activeId)}</div>

      <div className="border border-neutral-800 rounded-lg p-4 bg-neutral-950">
        <div className="flex items-center justify-between">
          <span className="text-sm font-bold">{t("j.technical")}</span>
          <span className="font-score text-4xl tabular text-white" data-testid="judge-technical">{technical.toFixed(1)}</span>
        </div>
        <div className="grid grid-cols-3 gap-2 mt-3">
          <Button className="h-20 bg-medical text-black hover:bg-medical/90 flex-col leading-tight" onClick={() => setTechnical((t2) => clamp(t2 + 0.1))} data-testid="tech-plus-01"><span className="text-xl font-bold">+0.1</span><span className="text-[11px]">{t("j.add")}</span></Button>
          <Button className="h-20 bg-hong hover:bg-hong/90 flex-col leading-tight" onClick={() => setTechnical((t2) => clamp(t2 - 0.3))} data-testid="tech-minus-03"><span className="text-xl font-bold">-0.3</span><span className="text-[11px]">{t("j.errSevere")}</span></Button>
          <Button className="h-20 bg-warn text-black hover:bg-warn/90 flex-col leading-tight" onClick={() => setTechnical((t2) => clamp(t2 - 0.1))} data-testid="tech-minus-01"><span className="text-xl font-bold">-0.1</span><span className="text-[11px]">{t("j.errLight")}</span></Button>
        </div>
      </div>

      <div className="space-y-5 mt-5">
        <Bar label={t("j.energy")} value={energy} set={setEnergy} testid="bar-energy" />
        <Bar label={t("j.balance")} value={balance} set={setBalance} testid="bar-balance" />
        <Bar label={t("j.rhythm")} value={rhythm} set={setRhythm} testid="bar-rhythm" />
      </div>

      <div className="flex items-center justify-between border-t border-neutral-800 mt-5 pt-4">
        <span className="text-sm uppercase tracking-widest text-neutral-500">{t("j.total")}</span>
        <span className="font-score text-5xl text-gold tabular" data-testid="judge-total">{total.toFixed(2)}</span>
      </div>
      <Button className="w-full h-14 mt-4 font-bold text-lg" onClick={submit} disabled={sent || (requireReady && !ready)} data-testid="judge-submit-poomsae">
        {sent ? <><CheckCircle weight="fill" className="mr-2" /> {t("j.sent")}</> : (requireReady && !ready) ? t("j.readyFirst") : t("j.send")}
      </Button>
    </div>
  );
}

function nameOf(m, id) {
  const a = m?.athletes?.[id];
  return a ? `${a.first_name} ${a.last_name}` : "—";
}
